// Session-state half of auth: the `ff_auth` sessionStorage contract (idToken +
// email) with client-side expiry checking. The sign-in flow itself (redirect
// OIDC) lives in src/auth.ts; the db client reads getIdToken() from here.
export interface AuthState {
	isAuthenticated: boolean;
	idToken: string | null;
	email: string | null;
}

const STORAGE_KEY = 'ff_auth';

// Parse JWT token without verification (for extracting claims like email, exp)
function parseJwt(token: string): Record<string, unknown> {
	const base64Url = token.split('.')[1];
	const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
	const jsonPayload = decodeURIComponent(
		atob(base64)
			.split('')
			.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
			.join('')
	);
	return JSON.parse(jsonPayload);
}

// Check if token is expired
function isTokenExpired(token: string): boolean {
	try {
		const claims = parseJwt(token);
		const exp = claims.exp as number;
		// Add 60 second buffer
		return Date.now() >= (exp - 60) * 1000;
	} catch {
		return true;
	}
}

// Get stored auth state
export function getStoredAuth(): AuthState {
	try {
		const stored = sessionStorage.getItem(STORAGE_KEY);
		if (!stored) {
			return { isAuthenticated: false, idToken: null, email: null };
		}

		const { idToken, email } = JSON.parse(stored);

		// Check if token is expired
		if (idToken && isTokenExpired(idToken)) {
			sessionStorage.removeItem(STORAGE_KEY);
			return { isAuthenticated: false, idToken: null, email: null };
		}

		return {
			isAuthenticated: !!idToken,
			idToken,
			email,
		};
	} catch {
		return { isAuthenticated: false, idToken: null, email: null };
	}
}

// Get ID token for API calls
export function getIdToken(): string | null {
	const auth = getStoredAuth();
	return auth.idToken;
}
